'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import { connect } from 'react-redux';
import { debounce, isEqual } from 'underscore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import CustomWidgetBuffer from 'DesignData/models/CustomWidgetBuffer';
import { guard } from 'DesignData/lib/functions';
import { INPUT_DEBOUNCE_DELAY } from 'DesignData/constants/FormsAndInput';
import { getUserHasReadAccessOnly } from 'DesignData/selectors/auth';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import * as customWidgetEditorOptionsSelectors from 'DesignData/selectors/customWidgetEditorOptions';
import * as customWidgetEditorOptionsActions from 'DesignData/actions/CustomWidgetEditorOptions';
import * as customWidgetBuffersActions from 'DesignData/actions/CustomWidgetBuffers';
import UIButton from 'UIComponents/button/UIButton';
import UIBreadcrumbs from 'UIComponents/nav/UIBreadcrumbs';
import FormCardContainer from 'DesignEditorsUI/components/shared/formCard/FormCardContainer';
import FormCardBody from 'DesignEditorsUI/components/shared/formCard/FormCardBody';
import FormCardHeader from 'DesignEditorsUI/components/shared/formCard/FormCardHeader';
import FormCardSection from 'DesignEditorsUI/components/shared/formCard/FormCardSection';
import FormCardWrapper from 'DesignEditorsUI/components/shared/formCard/FormCardWrapper';
import { KEYS, MODIFIERS } from 'DesignData/enums/Keys';
import Keybinding from 'DesignData/models/Keybinding';
import withKeyboardShortcuts from 'DesignComponents/decorators/withKeyboardShortcuts';
import { CriterionTypes as SmartCriterionTypes, TargetTypes as SmartTargetTypes } from 'smart-content-lib/constants';
import { getSmartObjectDataFromCriterion } from 'DesignData/helpers/smartObjectHelper';
import SmartDefinitionControls from 'DesignEditorsUI/components/shared/smartContent/SmartDefinitionControls';
import AsyncEditFormLoader from 'DesignEditorsUI/components/shared/AsyncEditFormLoader';
import { makeGetIsEmailOrAnyType } from 'DesignData/selectors/customWidgetBuffers';
const FormCardContainerWithKeybindings = withKeyboardShortcuts()(FormCardContainer);

function makeMapStateToProps() {
  const getCustomWidgetBuffer = customWidgetBuffersSelectors.makeGetCustomWidgetBuffer();
  const getCustomWidgetBufferFields = customWidgetBuffersSelectors.makeGetCustomWidgetBufferFields();
  const getIsEmailOrAnyType = makeGetIsEmailOrAnyType();
  const getSmartContentProps = customWidgetBuffersSelectors.makeGetSmartContentProps();
  const getSmartObjects = customWidgetBuffersSelectors.makeGetSmartObjects();
  const getIsReadOnly = customWidgetBuffersSelectors.makeGetIsReadOnly();
  return function mapStateToProps(state, props) {
    const fields = getCustomWidgetBufferFields(state, props);
    let customWidgetBuffer = getCustomWidgetBuffer(state, props);
    customWidgetBuffer = customWidgetBuffer ? customWidgetBuffer.set('fields', fields) : {
      fields,
      id: props.customWidgetId
    };
    const smartContentProps = getSmartContentProps(state, props);
    return Object.assign({
      isEmailOrAnyType: getIsEmailOrAnyType(state, props),
      isReadOnly: getUserHasReadAccessOnly(state) || getIsReadOnly(state, props),
      customWidgetBuffer,
      smartRuleIds: (smartContentProps.smartObjects || []).map(el => el.criterion_id),
      smartRulesById: getSmartObjects(state, props),
      selectedSmartRuleId: customWidgetEditorOptionsSelectors.getSelectedSmartRuleId(state, props)
    }, smartContentProps);
  };
}

const mapDispatchToProps = {
  setIsEditingGlobalContent: customWidgetEditorOptionsActions.setIsEditingGlobalContent,
  editGlobalContent: customWidgetBuffersActions.editGlobalContent,
  addSmartRule: customWidgetBuffersActions.addSmartRule,
  editSmartRule: customWidgetBuffersActions.editSmartRule,
  editSmartRulesOrder: customWidgetBuffersActions.editSmartRulesOrder,
  removeSmartRule: customWidgetBuffersActions.removeSmartRule,
  removeSmartRules: customWidgetBuffersActions.removeSmartRules,
  selectSmartRule: customWidgetEditorOptionsActions.selectSmartRule,
  updateSmartRules: customWidgetBuffersActions.updateSmartRules,
  makeSmart: customWidgetBuffersActions.makeSmart
};

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.globalContentCard.${key}`;

class GlobalContentCard extends PureComponent {
  constructor(props) {
    super(props);

    this.getGlobalContent = (customWidgetBuffer, selectedSmartRuleId) => {
      let globalContent = customWidgetBuffer.fields.reduce((_globalContent, _field) => {
        return Object.assign({}, _globalContent, {
          [_field.name]: _field.default
        });
      }, {});

      if (selectedSmartRuleId) {
        const selectedSmartRule = this.props.smartRulesById[selectedSmartRuleId] || {};
        globalContent = Object.assign({}, globalContent, {}, selectedSmartRule);
      }

      return globalContent;
    };

    this.editGlobalContent = newGlobalContent => {
      const prevGlobalContent = this.getGlobalContent(this.props.customWidgetBuffer, this.props.selectedSmartRuleId);
      const globalContentChanges = {};
      Object.keys(newGlobalContent).forEach(_fieldName => {
        const _newGlobalValue = newGlobalContent[_fieldName];

        if (!isEqual(prevGlobalContent[_fieldName], _newGlobalValue)) {
          globalContentChanges[_fieldName] = _newGlobalValue;
        }
      });
      this.props.editGlobalContent(this.props.customWidgetId, globalContentChanges);
    };

    this.handleCancel = () => {
      this.props.setIsEditingGlobalContent(this.props.customWidgetId, false);
    };

    this.keybindingConfig = {
      [MODIFIERS.NONE]: {
        [KEYS.ESCAPE]: new Keybinding({
          handler: this.handleCancel
        })
      }
    };

    this.handleFormUpdate = (key, value, globalContent) => {
      this.setState({
        globalContent
      });

      if (this.props.selectedSmartRuleId) {
        this.debouncedEditSmartRule(this.props.customWidgetId, this.props.selectedSmartRuleId, globalContent);
      } else {
        this.debouncedEditGlobalContent(globalContent);
      }
    };

    this.getSmartObjectData = criterion => {
      return getSmartObjectDataFromCriterion(criterion, SmartTargetTypes.WIDGET);
    };

    this.handleSelectSmartRule = (criterion = {}) => {
      const {
        customWidgetId,
        selectSmartRule,
        smartRulesById
      } = this.props;
      const {
        id: smartRuleId
      } = criterion;
      selectSmartRule(customWidgetId, smartRuleId);

      if (smartRuleId && !smartRulesById[smartRuleId]) {
        this.handleAddSmartRule(criterion);
      }
    };

    this.handleAddSmartRule = criterion => {
      const {
        customWidgetId,
        selectSmartRule,
        addSmartRule
      } = this.props;
      const smartObjectData = this.getSmartObjectData(criterion);
      addSmartRule(customWidgetId, smartObjectData);
      selectSmartRule(customWidgetId, criterion.id);
    };

    this.handleMakeSmart = ({
      definition,
      criterionType,
      initialCriterion
    }) => {
      const smartObjectData = {
        definitionId: definition.id,
        smartObjects: [this.getSmartObjectData(initialCriterion)],
        smartType: criterionType
      };
      this.props.makeSmart(this.props.customWidgetId, smartObjectData);
    };

    this.handleSaveSmartDefinition = (smartObjects, smartMetaData = {}) => {
      const {
        customWidgetId,
        updateSmartRules
      } = this.props;
      const newSmartObjects = smartObjects.map(this.getSmartObjectData);
      updateSmartRules(customWidgetId, newSmartObjects, smartMetaData);
    };

    const bindAndGuardReadOnly = guard.bind(null, this.guardReadOnly);
    this.handleFormUpdate = bindAndGuardReadOnly(this.handleFormUpdate, this);
    this.handleRemoveSelectedSmartRule = bindAndGuardReadOnly(this.handleRemoveSelectedSmartRule, this);
    this.handleAddSmartRule = bindAndGuardReadOnly(this.handleAddSmartRule, this);
    this.handleEditSmartRulesOrder = bindAndGuardReadOnly(this.handleEditSmartRulesOrder, this);
    this.handleRemoveAllSmartRules = bindAndGuardReadOnly(this.handleRemoveAllSmartRules, this);
    this.editGlobalContent = bindAndGuardReadOnly(this.editGlobalContent, this);
    this.debouncedEditGlobalContent = debounce(this.editGlobalContent, INPUT_DEBOUNCE_DELAY);
    this.handleMakeSmart = bindAndGuardReadOnly(this.handleMakeSmart, this);
    this.debouncedEditSmartRule = debounce(props.editSmartRule, INPUT_DEBOUNCE_DELAY);
    this.state = {
      globalContent: this.getGlobalContent(this.props.customWidgetBuffer, this.props.selectedSmartRuleId)
    };
  }

  componentDidUpdate(prevProps) {
    const {
      customWidgetId,
      revisionId,
      selectedSmartRuleId
    } = this.props;

    if (prevProps.customWidgetId !== customWidgetId || prevProps.revisionId !== revisionId || prevProps.selectedSmartRuleId !== selectedSmartRuleId) {
      this.setState({
        globalContent: this.getGlobalContent(this.props.customWidgetBuffer, this.props.selectedSmartRuleId)
      });
    }
  }

  get formKey() {
    const {
      customWidgetId,
      revisionId,
      selectedSmartRuleId
    } = this.props;
    const formKeyParts = [customWidgetId, revisionId, selectedSmartRuleId].filter(Boolean);
    return formKeyParts.join('-');
  }

  guardReadOnly() {
    return this.props.isReadOnly;
  }

  renderForm() {
    const {
      isReadOnly,
      customWidgetId,
      customWidgetBuffer
    } = this.props;
    return /*#__PURE__*/_jsx(AsyncEditFormLoader, {
      flush: false,
      id: `${customWidgetId}`,
      module: this.state.globalContent,
      moduleSpec: customWidgetBuffer,
      onUpdate: this.handleFormUpdate,
      readOnly: isReadOnly,
      hideLockedFields: false
    }, this.formKey);
  }

  renderSmartDefinitionControls() {
    const {
      isEligibleForSmartContent,
      isEmailOrAnyType,
      isReadOnly,
      customWidgetId,
      definitionId,
      selectedSmartRuleId,
      smartType,
      smartRuleIds,
      revisionId
    } = this.props;

    if (!isEligibleForSmartContent) {
      return null;
    }

    return /*#__PURE__*/_jsx("div", {
      className: "p-x-6 p-bottom-3",
      children: /*#__PURE__*/_jsx(SmartDefinitionControls, {
        anonymousTargeting: !isEmailOrAnyType,
        definitionId: definitionId,
        onAddRule: this.handleAddSmartRule,
        onSaveSmartDefinition: this.handleSaveSmartDefinition,
        onSelectRule: this.handleSelectSmartRule,
        readOnly: isReadOnly || !!revisionId,
        selectedRuleId: selectedSmartRuleId,
        smartType: smartType,
        targetId: customWidgetId,
        validRules: smartRuleIds
      }, `${this.formKey}-smart-controls`)
    });
  }

  render() {
    const titleI18nKey = this.props.isEligibleForSmartContent ? 'globalAndSmart' : 'global';

    const title = /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey(`title.${titleI18nKey}`),
      options: {
        label: this.props.customWidgetBuffer.name
      }
    });

    return /*#__PURE__*/_jsx(FormCardContainerWithKeybindings, {
      className: "custom-widget-global-content-card",
      keybindingConfig: this.keybindingConfig,
      children: /*#__PURE__*/_jsxs(FormCardWrapper, {
        children: [/*#__PURE__*/_jsx(FormCardSection, {
          className: "p-bottom-0",
          children: /*#__PURE__*/_jsx(UIBreadcrumbs, {
            className: "m-top-0",
            flush: true,
            children: /*#__PURE__*/_jsx(UIButton, {
              onClick: this.handleCancel,
              use: "link",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: i18nKey('backButtonText')
              })
            })
          })
        }), /*#__PURE__*/_jsx(FormCardHeader, {
          title: title
        }), /*#__PURE__*/_jsxs(FormCardBody, {
          flush: true,
          children: [this.renderSmartDefinitionControls(), this.renderForm()]
        })]
      })
    });
  }

}

GlobalContentCard.propTypes = {
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  revisionId: PropTypes.number,
  // @connect
  customWidgetBuffer: PropTypes.instanceOf(CustomWidgetBuffer).isRequired,
  isEmailOrAnyType: PropTypes.bool,
  isReadOnly: PropTypes.bool.isRequired,
  setIsEditingGlobalContent: PropTypes.func.isRequired,
  editGlobalContent: PropTypes.func.isRequired,
  // smart content props
  selectedSmartRuleId: PropTypes.number,
  definitionId: PropTypes.number,
  smartRuleIds: PropTypes.arrayOf(PropTypes.number),
  smartRulesById: PropTypes.object.isRequired,
  smartType: PropTypes.oneOf(Object.keys(SmartCriterionTypes)),
  addSmartRule: PropTypes.func.isRequired,
  editSmartRule: PropTypes.func.isRequired,
  selectSmartRule: PropTypes.func.isRequired,
  makeSmart: PropTypes.func.isRequired,
  isEligibleForSmartContent: PropTypes.bool.isRequired,
  updateSmartRules: PropTypes.func.isRequired
};
export default connect(makeMapStateToProps, mapDispatchToProps)(GlobalContentCard);